import { PublicHeader } from "@/components/layout/PublicHeader";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { usePageMeta } from "@/hooks/use-page-meta";

export default function Terms() {
  usePageMeta({
    title: "Terms & Conditions – Buukd",
    description:
      "Terms and conditions for using Buukd's booking and business management platform. Appointments, memberships, catering, and services.",
    path: "/terms",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <PublicHeader />
      
      <main className="flex-1 pt-24 pb-12">
        <div className="container-app max-w-4xl">
          <h1 className="text-4xl font-bold text-foreground mb-2">
            Terms & Conditions
          </h1>
          <p className="text-muted-foreground mb-8">
            Last Updated: {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
          </p>

          <div className="prose prose-slate max-w-none space-y-8 text-foreground">
            <section>
              <p className="text-muted-foreground">
                These Terms and Conditions ("Agreement") constitute a legally binding contract between Buukd ("we", "us", or "our") and you, the client ("Client", "you", or "your"), governing your access to and use of our booking and business management platform, including but not limited to:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>Appointment Booking & Scheduling</li>
                <li>Membership Management & Access Desk</li>
                <li>Catering Services & Order Management</li>
                <li>Event & Reservation Management</li>
                <li>Customer Relationship Management (CRM)</li>
                <li>Invoicing & Billing</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                (collectively, the "Platform" or "Services").
              </p>
              <p className="text-muted-foreground mt-4">
                By accessing or using the Services, you agree to these Terms. If you do not agree, you must immediately discontinue use of the Platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">1. DEFINITIONS & PARTIES</h2>
              <ul className="space-y-2 text-muted-foreground">
                <li><strong className="text-foreground">Service Provider:</strong> Buukd, operated by Razorbill Technologies, registered under the laws of the Republic of Ghana.</li>
                <li><strong className="text-foreground">Client:</strong> Any individual or business subscribing to or using the Services.</li>
                <li><strong className="text-foreground">Platform:</strong> Any web-based, desktop, or mobile software interface provided by Buukd.</li>
                <li><strong className="text-foreground">Authorized Users:</strong> Individuals granted access to the Platform under your subscription.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">2. ACCEPTANCE OF TERMS</h2>
              <p className="text-muted-foreground">
                By registering for or using the Platform, you confirm that you:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>Have legal authority to bind your organization (if applicable)</li>
                <li>Agree to comply with this Agreement</li>
                <li>Accept that Buukd may update these Terms, with notice provided via email or platform notification</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">3. INTELLECTUAL PROPERTY</h2>
              <p className="text-muted-foreground">
                All software, source code, designs, trademarks, and branding are the exclusive property of Buukd or its licensors. You may not:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>Reverse-engineer, copy, modify, or create derivative works</li>
                <li>Resell, sublicense, or distribute the Platform</li>
                <li>Use the Platform for unlawful or unauthorized purposes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">4. DATA OWNERSHIP & PRIVACY</h2>
              
              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">4.1 Client Data</h3>
              <p className="text-muted-foreground">
                You retain full ownership of all data entered into the Platform ("Client Data"), including customer information, appointments, memberships, orders, and business records. Buukd is granted a limited, non-exclusive license to process Client Data solely for the purpose of providing the Services.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">4.2 Privacy & Compliance</h3>
              <p className="text-muted-foreground">
                Buukd complies with:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>Ghana's Data Protection Act, 2012 (Act 843)</li>
                <li>GDPR (EU) and CCPA (California) where applicable</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                Client Data will not be shared except:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>With trusted service providers bound by confidentiality obligations</li>
                <li>Where required by law or legal process</li>
                <li>To prevent fraud or protect system security</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">4.3 International Transfers</h3>
              <p className="text-muted-foreground">
                Client Data may be processed or stored outside Ghana, including in the EU and the United States. Appropriate safeguards (e.g., Standard Contractual Clauses) are implemented to ensure compliance.
              </p>

              <h3 className="text-xl font-semibold text-foreground mt-6 mb-3">4.4 Data Retention & Deletion</h3>
              <p className="text-muted-foreground">
                Client Data is retained for the duration of your subscription. Upon termination:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>You may request a data export within 30 days</li>
                <li>After 30 days, Client Data will be permanently deleted unless legally required otherwise</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">5. LICENSE TO USE THE PLATFORM</h2>
              <p className="text-muted-foreground">
                You are granted a non-exclusive, non-transferable, revocable license to use the Platform solely for internal business purposes. You are responsible for all actions taken by your Authorized Users.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">6. FEES, BILLING & PAYMENT</h2>
              <ul className="space-y-2 text-muted-foreground">
                <li>Services are billed on a subscription basis (monthly, quarterly, or annually)</li>
                <li>Payments must be made to Buukd's designated account</li>
                <li>All subscriptions are non-refundable, unless otherwise determined at Buukd's discretion</li>
                <li>Non-payment exceeding 7 days may result in suspension or termination of Services</li>
                <li>Free trial periods are subject to the terms specified at signup</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">7. SERVICE AVAILABILITY & SUPPORT</h2>
              <ul className="space-y-2 text-muted-foreground">
                <li><strong className="text-foreground">Uptime Target:</strong> Buukd targets 99% platform availability, excluding scheduled maintenance and force majeure events</li>
                <li><strong className="text-foreground">Remedies:</strong> Failure to meet uptime targets does not automatically entitle Clients to refunds unless specified in a separate SLA</li>
                <li><strong className="text-foreground">Support:</strong> Customer support via email or phone, with urgent issues prioritized</li>
                <li><strong className="text-foreground">Updates:</strong> Platform updates, enhancements, and patches are included at no additional cost</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">8. ACCEPTABLE USE POLICY</h2>
              <p className="text-muted-foreground">
                You must not:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>Upload malware, exploit vulnerabilities, or bypass security controls</li>
                <li>Send spam, phishing attempts, or unauthorized communications</li>
                <li>Scrape, mine, or extract data without written consent</li>
                <li>Upload illegal, harmful, or infringing content</li>
                <li>Use the Platform to collect payments for illegal services or activities</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                Violations may result in immediate suspension or termination.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">9. THIRD-PARTY SERVICES</h2>
              <p className="text-muted-foreground">
                The Platform may integrate with third-party services (e.g., payment processors like Paystack, analytics tools, Google Maps). Such services are governed by their own terms and privacy policies. Buukd is not responsible for third-party availability, performance, or data handling.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">10. WARRANTIES & DISCLAIMERS</h2>
              <ul className="space-y-2 text-muted-foreground">
                <li>Buukd warrants that the Platform will materially perform as described</li>
                <li>The Platform is provided "as is" and "as available"</li>
                <li>All implied warranties, including merchantability, fitness for a particular purpose, and non-infringement, are disclaimed to the fullest extent permitted by law</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">11. LIMITATION OF LIABILITY</h2>
              <p className="text-muted-foreground">
                To the maximum extent permitted by law:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>Buukd shall not be liable for indirect, incidental, or consequential damages, including lost profits or data loss</li>
                <li>Total liability shall not exceed the subscription fees paid by the Client in the 12 months preceding the claim</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">12. INDEMNITY</h2>
              <p className="text-muted-foreground">
                You agree to indemnify and hold harmless Buukd, its officers, employees, and affiliates from any claims, damages, or losses arising from:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>Your use of the Platform</li>
                <li>Your breach of this Agreement</li>
                <li>Your violation of applicable laws or third-party rights</li>
                <li>Disputes between you and your customers or clients</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">13. FORCE MAJEURE</h2>
              <p className="text-muted-foreground">
                Buukd is not liable for failure or delay caused by events beyond reasonable control, including natural disasters, internet outages, strikes, pandemics, or government actions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">14. TERMINATION</h2>
              <p className="text-muted-foreground">
                Buukd may suspend or terminate Services if:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>You breach this Agreement</li>
                <li>You fail to make payment for more than 7 days</li>
                <li>You request cancellation</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                Upon termination:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
                <li>Client Data may be exported within 30 days</li>
                <li>After 30 days, Client Data will be permanently deleted unless legally required otherwise</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">15. GOVERNING LAW & DISPUTE RESOLUTION</h2>
              <ul className="space-y-2 text-muted-foreground">
                <li>This Agreement is governed by the laws of the Republic of Ghana</li>
                <li>Parties shall attempt informal resolution for 30 days</li>
                <li>If unresolved:
                  <ul className="list-disc pl-6 mt-2 space-y-2">
                    <li>Ghana-based Clients: Courts of Ghana</li>
                    <li>International Clients: Binding arbitration under ICC or LCIA rules, seated in Accra, Ghana, conducted in English</li>
                  </ul>
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">16. MODIFICATIONS</h2>
              <p className="text-muted-foreground">
                Buukd may modify these Terms at any time. Material changes will be communicated via email or platform notification. Continued use constitutes acceptance.
              </p>
            </section>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
